/**
 * modal.js — Slide-in drawer modal for header link content.
 * Xeric Theme
 *
 * API:
 *   XericModal.open(type, opts)  — slide the modal in with the given content
 *   XericModal.close()           — slide the modal back out
 *   XericModal.init()            — establish GSAP state + delegate all events
 *
 * Content modes (type parameter):
 *   'newsletter'   → reveals the pre-rendered Shopify customer form
 *   'page'         → lazy-fetches /pages/{handle}, caches, injects .page-inner
 *
 * Depends on: gsap.min.js (window.gsap)
 */

(function (global) {
  'use strict';

  // ── State ─────────────────────────────────────────────────────
  var _isOpen    = false;
  var _pageCache = {};   // handle → { html: string, title: string }

  // ── Cached DOM references (populated once in init) ────────────
  var _modal, _backdrop, _titleEl, _pageContent, _newsletter;

  // ── Reusable parser (one instance for the page lifetime) ──────
  var _parser = new DOMParser();

  // ── GSAP animation ────────────────────────────────────────────

  function killFruitingSeasonAnimations(scope) {
    if (!scope || !global.gsap) return;
    scope.querySelectorAll('[data-fs-animation-bound="true"]').forEach(function (el) {
      gsap.killTweensOf(el);
      el.removeAttribute('data-fs-animation-bound');
      el.style.removeProperty('filter');
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
      el.style.removeProperty('transform-origin');
    });
    scope.querySelectorAll('.fruiting-season__svg').forEach(function (wrap) {
      wrap.removeAttribute('data-fs-layers');
    });
  }

  function killExploreFlowAnimations(scope) {
    if (!scope || !global.gsap) return;
    scope.querySelectorAll('[data-es-animation-bound="true"]').forEach(function (el) {
      gsap.killTweensOf(el);
      el.removeAttribute('data-es-animation-bound');
      el.style.removeProperty('filter');
      el.style.removeProperty('opacity');
      el.style.removeProperty('transform');
      el.style.removeProperty('transform-origin');
    });
    scope.querySelectorAll('.explore-flow__svg').forEach(function (wrap) {
      wrap.removeAttribute('data-es-layers');
    });
  }

  function initFruitingSeasonAnimation(scope) {
    if (!scope || !global.gsap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var wrap = scope.querySelector('.fruiting-season__svg');
    if (!wrap) return;
    wrap.setAttribute('data-fs-anim', 'init');
    wrap.setAttribute('data-fs-layers', '');

    killFruitingSeasonAnimations(scope);

    // Keep a single visual asset in the wrapper to avoid stacked duplicates.
    var svgNodes = Array.prototype.slice.call(wrap.querySelectorAll('svg'));
    if (svgNodes.length > 1) {
      svgNodes.slice(1).forEach(function (node) { node.remove(); });
    }

    // Phase 1: collect animated targets (inline SVG preferred; fallback below).
    var svg = wrap.querySelector('svg');
    var arrows = [];
    var pairedStages = [];

    if (svg) {
      arrows = Array.prototype.slice.call(svg.querySelectorAll('.fs-arrow'));
      // Stage map: one arrow + one word group per phenology step.
      pairedStages = [1, 2, 3, 4, 5].map(function (index) {
        return {
          arrow: svg.querySelector('.fs-arrow--' + index),
          words: Array.prototype.slice.call(svg.querySelectorAll('.fs-word--' + index)),
        };
      });

      // Fallback for stale assets where arrow classes are missing.
      if (!arrows.length) {
        arrows = [
          svg.querySelector('path[d^="M514.623"]'),
          svg.querySelector('path[d^="M439.527"]'),
          svg.querySelector('path[d^="M106.354"]'),
          svg.querySelector('path[d^="M34.3699"]'),
          svg.querySelector('path[d^="M254.387"]'),
        ].filter(Boolean);
      }
    }

    // Phase 2 (fallback): animate a plain <img> when inline SVG is unavailable.
    if (!svg) {
      var imgs = Array.prototype.slice.call(wrap.querySelectorAll('img'));
      var img = imgs.length ? imgs[0] : null;

      if (imgs.length > 1) {
        imgs.slice(1).forEach(function (node) { node.remove(); });
      }

      // Self-heal: if fetched content has an empty wrapper, reconstruct the image.
      if (!img) {
        var fallbackUrl = wrap.getAttribute('data-fs-svg-url');
        if (fallbackUrl) {
          img = document.createElement('img');
          img.src = fallbackUrl;
          img.alt = 'Fruiting season illustration';
          img.loading = 'lazy';
          img.setAttribute('data-fs-generated', 'true');
          wrap.appendChild(img);
        }
      }

      if (!img) {
        wrap.setAttribute('data-fs-anim', 'none');
        wrap.setAttribute('data-fs-layers', 'none');
        return;
      }

      wrap.setAttribute('data-fs-anim', 'img');
      wrap.setAttribute('data-fs-layers', 'img-glow');
      img.setAttribute('data-fs-animation-bound', 'true');

      // Attempt to upgrade from <img> to inline SVG so arrow-level animations
      // can run even when Liquid falls back to image output.
      var inlineUrl = wrap.getAttribute('data-fs-svg-url');
      if (inlineUrl && !wrap.hasAttribute('data-fs-inline-attempted')) {
        wrap.setAttribute('data-fs-inline-attempted', 'true');
        fetch(inlineUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (res) {
            if (!res.ok) throw new Error('svg-fetch-failed');
            return res.text();
          })
          .then(function (raw) {
            if (!raw || raw.indexOf('<svg') === -1) return;

            // Resolve nested asset refs (for example the center logo) against
            // the fetched SVG URL so they don't break after inline injection.
            try {
              var fetchedSvgUrl = new URL(inlineUrl, window.location.origin);
              var logoAssetUrl = fetchedSvgUrl.href.replace(
                /FruitingSeasonHardcode\.svg(?:\?.*)?$/i,
                'FruitingSeasonHardcodeLogo.svg'
              );
              raw = raw.replace(/FruitingSeasonHardcodeLogo\.svg/g, logoAssetUrl);
            } catch (e) {
              // If URL normalization fails, keep raw SVG as-is.
            }

            wrap.innerHTML = raw;
            wrap.setAttribute('data-fs-anim', 'svg-inline-hydrated');
            initFruitingSeasonAnimation(scope);
          })
          .catch(function () {
            // Keep img-mode animation as fallback when inline hydration fails.
          });
      }

      // Keep fallback image static; only animate glow.
      gsap.to(img, {
        filter: 'drop-shadow(0 0 9px rgba(129, 143, 176, 0.73))',
        duration: 1.1,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
      return;
    }

    // Phase 3: inline-SVG mode (full layered animation system).
    // Remove image fallbacks so only one visual source is animated.
    Array.prototype.slice.call(wrap.querySelectorAll('img')).forEach(function (node) {
      node.remove();
    });

    svg.setAttribute('data-fs-animation-bound', 'true');
    wrap.setAttribute('data-fs-animation-bound', 'true');
    wrap.setAttribute('data-fs-anim', 'svg');
    arrows.forEach(function (arrow) {
      arrow.setAttribute('data-fs-animation-bound', 'true');
    });
    var pairedWordPaths = pairedStages.reduce(function (acc, stage) {
      return acc.concat(stage.words || []);
    }, []);
    var bodyPaths = Array.prototype.slice.call(svg.querySelectorAll('path:not(.fs-arrow):not(.fs-word)'));
    bodyPaths.forEach(function (path) {
      path.setAttribute('data-fs-animation-bound', 'true');
    });

    gsap.set(svg, { transformOrigin: '50% 50%' });
    if (arrows.length) {
      // Baseline state for directional arrows before timeline pulses begin.
      gsap.set(arrows, {
        transformOrigin: '50% 50%',
        transformBox: 'fill-box',
        opacity: 0,
        stroke: 'rgba(69, 82, 50, 0)',
        strokeWidth: 0,
        filter: 'drop-shadow(0 0 0 rgba(42,59,99,0))',
      });
    }

    if (pairedWordPaths.length) {
      // Baseline state for stage labels that pulse with matching arrows.
      gsap.set(pairedWordPaths, {
        transformOrigin: '50% 50%',
        transformBox: 'fill-box',
        opacity: 0.84,
        filter: 'drop-shadow(0 0 0 rgba(131,158,94,0))',
      });
    }

    if (!arrows.length) {
      wrap.setAttribute('data-fs-layers', 'static');
      return;
    }

    // Layer 3: staged pulse timeline (step-by-step phenology flow).
    // Keep this cadence aligned with Explore: one stage at a time.
    var cycle = gsap.timeline({ repeat: -1, repeatDelay: 0.25 });

    // Start the cycle from seeds, then continue through the remaining stages.
    var stagePulseOrder = [4, 5, 1, 2, 3].map(function (index) {
      return pairedStages[index - 1];
    }).filter(Boolean);

    stagePulseOrder.forEach(function (stage) {
      var targets = [];
      if (stage.arrow) targets.push(stage.arrow);
      if (stage.words && stage.words.length) targets = targets.concat(stage.words);
      if (!targets.length) return;

      var arrowTargets = stage.arrow ? [stage.arrow] : [];
      var wordTargets = stage.words || [];
      // Pulse in: arrow + paired word glow together.
      cycle.to(targets, {
        filter: 'drop-shadow(0 0 7px rgba(131,158,94,0.5))',
        duration: 0.5,
        ease: 'power1.out',
      });
      if (arrowTargets.length) {
        // Arrow pulse: pure visibility transition (hidden -> visible).
        cycle.to(arrowTargets, {
          opacity: 1,
          duration: 0.5,
          ease: 'power1.out',
        }, '<');
      }
      if (wordTargets.length) {
        // Faux "bold" on SVG paths (font-weight is unavailable for path text).
        cycle.to(wordTargets, {
          stroke: 'rgb(69, 82, 50)',
          strokeWidth: 1.00,
          duration: 0.5,
          ease: 'power1.out',
        }, '<');
      }
      // Pulse out: return to baseline glow before next stage.
      cycle.to(targets, {
        filter: 'drop-shadow(0 0 0 rgba(131,158,94,0))',
        duration: 0.62,
        ease: 'power1.inOut',
      });
      if (arrowTargets.length) {
        cycle.to(arrowTargets, {
          opacity: 0,
          duration: 0.62,
          ease: 'power1.inOut',
        }, '<');
      }
      if (wordTargets.length) {
        // Remove stroke-weight boost to restore normal label weight.
        cycle.to(wordTargets, {
          stroke: 'rgba(131,158,94,0)',
          strokeWidth: 0,
          duration: 0.62,
          ease: 'power1.inOut',
        }, '<');
      }

      // Small gap before next stage to keep pulses visually isolated.
      cycle.to({}, { duration: 0.06 });
    });

    wrap.setAttribute(
      'data-fs-layers',
      'paired-stage-pulse'
    );
  }

  function initExploreFlowAnimation(scope) {
    if (!scope || !global.gsap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var wrap = scope.querySelector('.explore-flow__svg');
    if (!wrap) return;
    wrap.setAttribute('data-es-anim', 'init');
    wrap.setAttribute('data-es-layers', '');

    killExploreFlowAnimations(scope);

    var svgNodes = Array.prototype.slice.call(wrap.querySelectorAll('svg'));
    if (svgNodes.length > 1) {
      svgNodes.slice(1).forEach(function (node) { node.remove(); });
    }

    var svg = wrap.querySelector('svg');
    if (!svg) {
      var imgs = Array.prototype.slice.call(wrap.querySelectorAll('img'));
      var img = imgs.length ? imgs[0] : null;

      if (imgs.length > 1) {
        imgs.slice(1).forEach(function (node) { node.remove(); });
      }

      if (!img) {
        var fallbackUrl = wrap.getAttribute('data-explore-svg-url');
        if (fallbackUrl) {
          img = document.createElement('img');
          img.src = fallbackUrl;
          img.alt = 'Explore flow illustration';
          img.loading = 'lazy';
          img.setAttribute('data-es-generated', 'true');
          wrap.appendChild(img);
        }
      }

      if (!img) {
        wrap.setAttribute('data-es-anim', 'none');
        wrap.setAttribute('data-es-layers', 'none');
        return;
      }

      wrap.setAttribute('data-es-anim', 'img');
      wrap.setAttribute('data-es-layers', 'img-glow');
      img.setAttribute('data-es-animation-bound', 'true');

      var inlineUrl = wrap.getAttribute('data-explore-svg-url');
      if (inlineUrl && !wrap.hasAttribute('data-es-inline-attempted')) {
        wrap.setAttribute('data-es-inline-attempted', 'true');
        fetch(inlineUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (res) {
            if (!res.ok) throw new Error('svg-fetch-failed');
            return res.text();
          })
          .then(function (raw) {
            if (!raw || raw.indexOf('<svg') === -1) return;
            wrap.innerHTML = raw;
            wrap.setAttribute('data-es-anim', 'svg-inline-hydrated');
            initExploreFlowAnimation(scope);
          })
          .catch(function () {
            // Keep img mode as the fallback.
          });
      }

      gsap.to(img, {
        filter: 'drop-shadow(0 0 10px rgba(148, 166, 209, 0.72))',
        duration: 1.1,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
      return;
    }

    Array.prototype.slice.call(wrap.querySelectorAll('img')).forEach(function (node) {
      node.remove();
    });

    var pairedStages = [1, 2, 3, 4, 5].map(function (index) {
      return {
        step: svg.querySelector('.es-step--' + index),
        // Arrows are visually between blocks; each arrow belongs to the block below it.
        // step 1 has no paired arrow, step 2 uses arrow 1 ... step 5 uses arrow 4.
        arrow: index > 1 ? svg.querySelector('.es-arrow--' + (index - 1)) : null,
      };
    });

    var arrows = pairedStages.map(function (stage) { return stage.arrow; }).filter(Boolean);
    var steps = pairedStages.map(function (stage) { return stage.step; }).filter(Boolean);
    var labels = Array.prototype.slice.call(svg.querySelectorAll('.es-label'));

    svg.setAttribute('data-es-animation-bound', 'true');
    wrap.setAttribute('data-es-animation-bound', 'true');
    wrap.setAttribute('data-es-anim', 'svg');
    arrows.forEach(function (arrow) {
      arrow.setAttribute('data-es-animation-bound', 'true');
    });
    steps.forEach(function (step) {
      step.setAttribute('data-es-animation-bound', 'true');
    });
    labels.forEach(function (label) {
      label.setAttribute('data-es-animation-bound', 'true');
    });

    if (arrows.length) {
      gsap.set(arrows, {
        opacity: 0,
        stroke: 'rgba(69, 82, 50, 0)',
        strokeWidth: 0,
        filter: 'drop-shadow(0 0 0 rgba(148,166,209,0))',
      });
    }

    if (steps.length) {
      gsap.set(steps, {
        opacity: 0.9,
        filter: 'drop-shadow(0 0 0 rgba(148,166,209,0))',
      });
    }

    if (labels.length) {
      gsap.set(labels, {
        stroke: 'rgba(103,102,87,0)',
        strokeWidth: 0,
      });
    }

    var cycle = gsap.timeline({ repeat: -1, repeatDelay: 0.25 });
    // Run Explore flow at half speed while preserving existing keyframes.
    cycle.timeScale(0.5);

    var pulseInDuration = 0.5;
    var resetDuration = 0.62;

    pairedStages.forEach(function (stage, stageIndex) {
      var stepTargets = [];
      var labelTargets = [];

      if (stage.step) {
        stepTargets.push(stage.step);
        labelTargets = Array.prototype.slice.call(stage.step.querySelectorAll('.es-label'));
      }
      if (!stepTargets.length && !stage.arrow) return;

      if (stepTargets.length) {
        cycle.to(stepTargets, {
          opacity: 1,
          filter: 'drop-shadow(0 0 10px rgba(148,166,209,0.72))',
          duration: pulseInDuration,
          ease: 'power1.out',
        });
      }

      if (stage.arrow) {
        // Arrow pulse: pure visibility transition (hidden -> visible -> hidden).
        cycle.to(stage.arrow, {
          opacity: 1,
          duration: pulseInDuration,
          ease: 'power1.out',
        }, '<');
      }

      if (labelTargets.length) {
        cycle.to(labelTargets, {
          stroke: 'rgb(103, 102, 87)',
          strokeWidth: 1,
          duration: pulseInDuration,
          ease: 'power1.out',
        }, '<');
      }

      // Stage reset: all animated elements return together.
      var resetLabel = 'es-reset-' + stageIndex;

      if (stepTargets.length) {
        cycle.to(stepTargets, {
          opacity: 0.9,
          filter: 'drop-shadow(0 0 0 rgba(148,166,209,0))',
          duration: resetDuration,
          ease: 'power1.inOut',
        }, resetLabel);
      }

      if (stage.arrow) {
        cycle.to(stage.arrow, {
          opacity: 0,
          duration: resetDuration,
          ease: 'power1.inOut',
        }, resetLabel);
      }

      if (labelTargets.length) {
        cycle.to(labelTargets, {
          stroke: 'rgba(103,102,87,0)',
          strokeWidth: 0,
          duration: resetDuration,
          ease: 'power1.inOut',
        }, resetLabel);
      }

      // Small gap before next stage to keep pulses visually isolated.
      cycle.to({}, { duration: 0.06 });
    });

    wrap.setAttribute('data-es-layers', 'paired-stage-pulse');
  }

  function animateOpen() {
    if (!_modal) return;
    _modal.setAttribute('aria-hidden', 'false');
    // overwrite:true replaces manual _tween.kill(); force3D keeps the element
    // on the GPU compositor layer throughout the transition.
    gsap.to(_modal, {
      x:         0,
      duration:  0.45,
      ease:      'power2.out',
      overwrite: true,
      force3D:   true,
    });
  }

  function animateClose() {
    if (!_modal) return;
    gsap.to(_modal, {
      x:         '100%',
      duration:  0.35,
      ease:      'power2.in',
      overwrite: true,
      force3D:   true,
      onComplete: function () {
        _modal.setAttribute('aria-hidden', 'true');
      },
    });
  }

  // ── Page content fetch ────────────────────────────────────────

  async function loadPageContent(handle, contentEl, titleEl) {
    // Serve from cache on repeat opens.
    // Defer the innerHTML write to rAF so the opening animation's first frame
    // fires on a clean layout budget rather than competing with a DOM mutation.
    if (_pageCache[handle]) {
      requestAnimationFrame(function () {
        contentEl.innerHTML = _pageCache[handle].html;
        if (titleEl && !titleEl.textContent.trim()) {
          titleEl.textContent = _pageCache[handle].title;
        }
        initFruitingSeasonAnimation(contentEl);
        initExploreFlowAnimation(contentEl);
      });
      return;
    }

    try {
      // credentials:'same-origin' is the fetch default for same-origin URLs;
      // omitting it avoids the redundant option-parsing overhead.
      var res = await fetch('/pages/' + handle, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!res.ok) throw new Error(res.status);

      var html = await res.text();
      var doc  = _parser.parseFromString(html, 'text/html');

      // Extract the .page-inner wrapper (including the element itself so its
      // CSS class selectors in theme.css still match the injected content).
      var inner   = doc.querySelector('.page-inner');
      var content = inner ? inner.outerHTML
                          : (doc.querySelector('main') || doc.body).innerHTML;

      // Derive display title: prefer first <h1>, fall back to <title> sans shop name
      var h1     = doc.querySelector('.page-inner h1, main h1, h1');
      var title  = h1
        ? h1.textContent.trim()
        : (doc.title || handle).split(/\s*[–—|]\s*/)[0].trim();

      _pageCache[handle] = { html: content, title: title };
      contentEl.innerHTML = content;

      if (titleEl && !titleEl.textContent.trim()) {
        titleEl.textContent = title;
      }
      initFruitingSeasonAnimation(contentEl);
      initExploreFlowAnimation(contentEl);
    } catch (e) {
      contentEl.innerHTML = '<p class="modal__error">Content could not be loaded.</p>';
    }
  }

  // ── Public API ────────────────────────────────────────────────

  function open(type, opts) {
    opts = opts || {};
    if (!_modal) return;

    _isOpen = true;

    // Activate backdrop (invisible click-to-close surface)
    if (_backdrop) _backdrop.classList.add('is-active');

    if (type === 'newsletter') {
      _modal.classList.remove('site-modal--fruiting-season');
      _modal.classList.remove('site-modal--explore');
      if (_titleEl)     _titleEl.textContent = opts.title || 'Newsletter';
      if (_pageContent) {
        killFruitingSeasonAnimations(_pageContent);
        killExploreFlowAnimations(_pageContent);
        _pageContent.innerHTML = '';
        _pageContent.hidden = true;
      }
      if (_newsletter)  _newsletter.hidden = false;
      animateOpen();

    } else {
      // Page fetch mode
      var handle = opts.handle || type;
      _modal.classList.toggle('site-modal--fruiting-season', handle === 'fruiting-season');
      _modal.classList.toggle('site-modal--explore', handle === 'explore');
      if (_titleEl)     _titleEl.textContent = opts.title || '';
      if (_newsletter)  _newsletter.hidden = true;
      if (_pageContent) {
        _pageContent.hidden = false;
        // Show spinner immediately if not yet cached
        if (!_pageCache[handle]) {
          _pageContent.innerHTML = '<p class="modal__loading">Loading\u2026</p>';
        }
      }
      // Animate open immediately; content populates in parallel
      animateOpen();
      if (_pageContent) loadPageContent(handle, _pageContent, _titleEl);
    }
  }

  function close() {
    if (!_isOpen) return;
    _isOpen = false;
    if (_modal) _modal.classList.remove('site-modal--fruiting-season');
    if (_modal) _modal.classList.remove('site-modal--explore');
    if (_pageContent) killFruitingSeasonAnimations(_pageContent);
    if (_pageContent) killExploreFlowAnimations(_pageContent);
    if (_backdrop) _backdrop.classList.remove('is-active');
    animateClose();
  }

  // ── Debounce helper ───────────────────────────────────────────
  function debounce(fn, ms) {
    var timer;
    return function () { clearTimeout(timer); timer = setTimeout(fn, ms); };
  }

  function init() {
    // Populate cached DOM references once — all subsequent code uses these
    // variables directly rather than calling getElementById on every interaction.
    _modal       = document.getElementById('site-modal');
    _backdrop    = document.getElementById('modal-backdrop');
    _titleEl     = document.getElementById('modal-title');
    _pageContent = document.getElementById('modal-page-content');
    _newsletter  = document.getElementById('modal-newsletter');

    if (!_modal) return;

    // Set initial off-screen position; force3D promotes the element to its own
    // GPU compositor layer from frame 0 so the first animation tick is free.
    gsap.set(_modal, { x: '100%', force3D: true });

    // ── Event delegation ─────────────────────────────────────
    // Capture phase fires before Barba's bubble-phase listener;
    // stopImmediatePropagation() prevents Barba from ever seeing modal clicks.
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-modal]');
      if (!link) return;
      e.preventDefault();
      e.stopImmediatePropagation();

      var type   = link.dataset.modal;
      var handle = link.dataset.modalHandle || type;
      var title  = link.dataset.modalTitle  ||
                   (link.querySelector('.header__link-text') || {}).textContent || '';
      var mobileTitle = link.dataset.modalTitleMobile || '';
      var resolvedTitle = title;

      if (mobileTitle && window.matchMedia('(max-width: 768px)').matches) {
        resolvedTitle = mobileTitle;
      }

      open(type, { handle: handle, title: resolvedTitle.trim() });
    }, true /* capture */);

    // Close button
    document.addEventListener('click', function (e) {
      if (e.target.closest('#modal-close')) close();
    });

    // Backdrop — the entire invisible div acts as a close button
    if (_backdrop) _backdrop.addEventListener('click', close);

    // Keyboard: Escape closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && _isOpen) close();
    });

    // Keep GSAP transform in sync if window is resized while closed.
    // Debounced to 100 ms — collapses rapid-fire resize events into one call.
    window.addEventListener('resize', debounce(function () {
      if (!_isOpen) gsap.set(_modal, { x: '100%', force3D: true });
    }, 100));
  }

  global.XericModal = { open: open, close: close, init: init };

})(window);
